import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Target, Trophy, Clock, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";

interface ProgressData {
  total_interviews: number;
  total_questions: number;
  average_score: number;
  practice_streak: number;
  last_practice_date: string;
  skill_scores: {
    behavioral: number;
    technical: number;
    situational: number;
    communication: number;
    leadership: number;
  };
  badges: Array<{
    id: string;
    name: string;
    description: string;
    earned_at: string;
  }>;
}

const InterviewAnalytics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("30");

  useEffect(() => {
    loadAnalytics();
  }, [timePeriod]);

  const loadAnalytics = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data, error } = await supabase
        .from('interview_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const skillScores = data.skill_scores as any || {
          behavioral: 0,
          technical: 0,
          situational: 0,
          communication: 0,
          leadership: 0
        };
        
        const badges = (data.badges as any) || [];
        
        setProgress({
          total_interviews: data.total_interviews || 0,
          total_questions: data.total_questions || 0,
          average_score: data.average_score || 0,
          practice_streak: data.practice_streak || 0,
          last_practice_date: data.last_practice_date || '',
          skill_scores: skillScores,
          badges: badges
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const skillCategories = [
    { key: 'behavioral', label: 'Behavioral Questions', icon: '💬' },
    { key: 'technical', label: 'Technical Questions', icon: '💻' },
    { key: 'situational', label: 'Situational Questions', icon: '🎯' },
    { key: 'communication', label: 'Communication', icon: '🗣️' },
    { key: 'leadership', label: 'Leadership', icon: '👔' }
  ];

  const availableBadges = [
    { id: 'first_interview', name: 'First Interview', description: 'Completed first practice', icon: '🏆' },
    { id: 'streak_7', name: '7-Day Streak', description: 'Practiced 7 days in a row', icon: '🔥' },
    { id: 'perfect_score', name: 'Perfect Score', description: 'Got 10/10 on an answer', icon: '⭐' },
    { id: 'improvement_master', name: 'Improvement Master', description: 'Improved score by 20%', icon: '💪' },
    { id: 'question_master', name: 'Question Master', description: 'Scored 9+ on 50 questions', icon: '🎯' },
    { id: 'interview_pro', name: 'Interview Pro', description: 'Completed 25 interviews', icon: '🚀' }
  ];

  if (loading || !progress) {
    return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading analytics...</p>
          </div>
        </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Progress Analytics | AI Interview Prep</title>
      </Helmet>

      
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
          <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold">Progress Analytics</h1>
                <p className="text-muted-foreground mt-2">
                  Track your improvement and celebrate milestones
                </p>
              </div>
              <div className="flex gap-4">
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 3 months</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => navigate('/app/ai-interview-prep')}>
                  Back to AI Interview Prep
                </Button>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6 text-center">
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-3xl font-bold">{progress.total_interviews}</div>
                  <div className="text-sm text-muted-foreground">Total Interviews</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Target className="w-8 h-8 mx-auto mb-2 text-accent" />
                  <div className="text-3xl font-bold">{progress.average_score.toFixed(1)}/10</div>
                  <div className="text-sm text-muted-foreground">Average Score</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                  <div className="text-3xl font-bold">{progress.total_questions}</div>
                  <div className="text-sm text-muted-foreground">Questions Practiced</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <div className="text-3xl font-bold">{progress.practice_streak} 🔥</div>
                  <div className="text-sm text-muted-foreground">Day Streak</div>
                </CardContent>
              </Card>
            </div>

            {/* Skill Development Tracker */}
            <Card>
              <CardHeader>
                <CardTitle>Skill Development</CardTitle>
                <CardDescription>Your performance across different skill areas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {skillCategories.map((skill) => {
                  const score = progress.skill_scores[skill.key as keyof typeof progress.skill_scores] || 0;
                  return (
                    <div key={skill.key} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium flex items-center gap-2">
                          <span>{skill.icon}</span>
                          {skill.label}
                        </span>
                        <span className="text-sm font-bold">{score}%</span>
                      </div>
                      <Progress value={score} className="h-2" />
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Achievement Badges */}
            <Card>
              <CardHeader>
                <CardTitle>Achievement Badges</CardTitle>
                <CardDescription>Unlock badges as you progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {availableBadges.map((badge) => {
                    const isEarned = progress.badges.some(b => b.id === badge.id);
                    return (
                      <Card key={badge.id} className={!isEarned ? 'opacity-50' : ''}>
                        <CardContent className="p-6 text-center">
                          <div className="text-4xl mb-2">{badge.icon}</div>
                          <div className="font-semibold">{badge.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {badge.description}
                          </div>
                          {isEarned && (
                            <Badge variant="secondary" className="mt-2">Earned</Badge>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Comparison Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Comparison</CardTitle>
                <CardDescription>See how you compare to other users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-primary/5 rounded">
                  <div>
                    <div className="font-semibold">Your Average Score</div>
                    <div className="text-sm text-muted-foreground">
                      {progress.average_score.toFixed(1)}/10
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">Platform Average</div>
                    <div className="text-sm text-muted-foreground">7.1/10</div>
                  </div>
                </div>
                <div className="p-4 border rounded text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <div className="font-semibold text-lg">
                    You're in the top 25% of users!
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Keep up the great work
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      
    </>
  );
};

export default InterviewAnalytics;
